import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AnimatePresence, motion } from 'framer-motion';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import AuthCallback from './components/AuthCallback';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import AirportPage from './pages/AirportPage';
import Discover from './pages/Discover';
import Profile from './pages/Profile';
import Premium from './pages/Premium';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Admin from './pages/Admin';
import About from './pages/About';
import Contact from './pages/Contact';
import HowItWorks from './pages/HowItWorks';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Safety from './pages/Safety';
import CookiePolicy from './pages/CookiePolicy';
import Community from './pages/Community';
import BuddyMatcher from './pages/BuddyMatcher';
import Airlines from './pages/Airlines';
import RouteCommunity from './pages/RouteCommunity';
import RoutesPage from './pages/Routes';
import FlightTracker from './pages/FlightTracker';
import MobileBottomNav from './components/MobileBottomNav';
import AddToHomeScreen from './components/AddToHomeScreen';
import FlightLogModal from './components/FlightLogModal';
import MobileFAB from './components/MobileFAB';
import { FlightLogProvider } from './contexts/FlightLogContext';
import './App.css';

function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously before routes render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -5 }}
        transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
        style={{ width: '100%' }}
      >
        <Routes location={location}>
          <Route path="/" element={<Landing />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/dashboard" element={<Dashboard />} />
          {/* Legacy flight chat routes → redirect to route community */}
          <Route path="/flight/:roomId" element={<FlightRedirect />} />
          <Route path="/airport/:airportCode" element={<AirportPage />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/profile/:userId" element={<Profile />} />
          <Route path="/premium" element={<Premium />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/safety" element={<Safety />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/community" element={<Community />} />
          <Route path="/airlines" element={<Airlines />} />
          <Route path="/route/:routeId" element={<RouteCommunity />} />
          <Route path="/routes" element={<RoutesPage />} />
          <Route path="/tracker" element={<FlightTracker />} />
          <Route path="/buddy" element={<BuddyMatcher />} />
          <Route path="*" element={<Landing />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

/** Redirect old /flight/flight_EK101 → /route/DXB-LHR (or /route/EK101 as fallback) */
function FlightRedirect() {
  const location = useLocation();
  const roomId = location.pathname.split('/flight/')[1] || '';
  // Extract flight number from room ID like "flight_EK101" → "EK101"
  const flightNum = roomId.replace('flight_', '').toUpperCase();
  // Redirect to route with flight number as route ID (RouteCommunity handles lookup)
  return <Navigate to={`/route/${flightNum}`} replace />;
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AuthProvider>
          <FlightLogProvider>
            <BrowserRouter>
              <div className="App">
                <AppRouter />
                <MobileBottomNav />
                <MobileFAB />
                <FlightLogModal />
                <AddToHomeScreen />
                <Toaster position="top-right" richColors />
              </div>
            </BrowserRouter>
          </FlightLogProvider>
        </AuthProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
